import './index.css'

import lodashIntersection from 'lodash/intersection'
import lodashUniqBy from 'lodash/uniqBy'
import React, { Component } from 'react'
import { Link } from 'react-router-dom'

import captureError from '@disco/utils/captureError'
import { getRelativeTime } from '@disco/utils/date'
import dayjs from '@disco/utils/dayjs'

import type { BriefSubmissionNotification } from 'types/notification'
import type { TrackId } from 'types/track'

import HelpTooltip from 'components/common/tooltip'
import Button from 'components/ui/button'
import Icon from 'components/ui/icon'

type NotificationObject = {
  id?: number | null | undefined
  name: string
}
type NotificationEvent = {
  user: NotificationObject
  timestamp: string
  track: NotificationObject
}
type TBaseNotification = {
  timestamp: string
  sender_name: string
  accessor_name?: string // TODO: DEPRECATED ('accessor_name' is not used)
  url?: string
}
type TAggregatedNotificationItem = TBaseNotification & {
  type: 'aggregated'
  url: string
  marked_as_read: string
  // timestamp
  playlist_id: number
  playlist_name: string
  playlist_accesses: NotificationEvent[]
  playlist_downloads: NotificationEvent[]
  track_downloads: NotificationEvent[]
  track_streams: NotificationEvent[]
  total_playlist_accesses_num: number
  recent_playlist_accesses_num: number
  total_track_downloads_num: number
  recent_track_downloads_num: number
  total_track_streams_num: number
  recent_track_streams_num: number
  total_playlist_downloads_num: number
  recent_playlist_downloads_num: number
}
type TNotificationItem =
  | TAggregatedNotificationItem
  | (TBaseNotification & {
      type: 'client_library_access_requested'
      client_library_name: string
      client_library_id: number
      sender_name: string
      sender_email: string
    })
  | (TBaseNotification & {
      type: 'client_library_access_granted'
      client_library_name: string
      client_library_url: string
      business: {
        name: string
      }
      sender_name: string
      sender_email: string
    })
  | (TBaseNotification & {
      type: 'bulk_upload_complete'
      content_label: string
    })
  | (TBaseNotification & {
      type: 'add_to_disco_complete'
      content_label: string
    })
  | (TBaseNotification & {
      type: 'receive_upload'
      playlist: NotificationObject
      sender_name: string
    })
  | (TBaseNotification & {
      type: 'group_receive_upload'
      playlist: NotificationObject
      sender_name: string
    })
  | (TBaseNotification & {
      type: 'send_upload'
      playlist: NotificationObject
      sender_name: string
    })
  | (TBaseNotification & {
      type: 'receive_share'
      content_label: string
      sender_name: string
      url: string
      redirect_to_public_page: boolean
    })
  | (TBaseNotification & {
      type: 'group_receive_share'
      content_label: string
      sender_name: string
    })
  | (TBaseNotification & {
      // TODO: DEPRECATED ('access_share' is not used)
      type: 'access_share'
      accessor_name: string
      content_label: string
    })
  | (TBaseNotification & {
      type: 'aggregated_stats_track_streams'
      track_streams: NotificationEvent[]
      timestamp: string
    })
  | (TBaseNotification & {
      type: 'aggregated_stats_track_downloads'
      track_downloads: NotificationEvent[]
      timestamp: string
    })
  | (TBaseNotification & {
      type: 'aggregated_stats_playlist_downloads'
      playlist_downloads: NotificationEvent[]
      timestamp: string
    })
  | (TBaseNotification & {
      type: 'aggregated_stats_playlist_accesses'
      playlist_accesses: NotificationEvent[]
      timestamp: string
    })
  | (TBaseNotification & {
      type: 'watermark_leak_trace_update'
      message_id: number
      data: {
        id: number
        status: 'completed'
      }
    })
  | (TBaseNotification & {
      type: 'licensing_term_notifications'
      track: {
        id?: TrackId
        app_url: string
        name: string
      }
      expiration_date: string
    })
  | (TBaseNotification & {
      type: 'welcome_to_disco'
      playlist: NotificationObject
    })
  | (TBaseNotification & {
      type: 'channel_share_invite'
      channel_id: string
      inviting_business_name: string
      channel_name: string
    })
  | (TBaseNotification & {
      type: 'channel_share_invite_accepted'
      invited_business_name: string
      channel_name: string
    })
  | BriefSubmissionNotification

type Props = {
  item: TNotificationItem
  expanded?: boolean
  timelineItem: React.ReactNode
  hasDismissButton: boolean
  onExpandNotification: (arg0: TNotificationItem) => void
  onOpenLibraryClients: (arg0: TNotificationItem) => void
  onOpenNotification: (arg0: TNotificationItem) => void
  onOpenPlaylist: (arg0: TNotificationItem) => void
  onOpenBriefSubmission: (arg0: BriefSubmissionNotification) => void
  onAcceptChannelInvite: (arg0: TNotificationItem) => void
  onDismiss: (arg0: TNotificationItem) => void
  onOpenTrackRoute: (arg0: TNotificationItem) => void
  onClickLink: (arg0: TNotificationItem) => void
}
type State = {
  isDismissing: boolean
}
const MAX_NAME_LENGTH = 20

const plural = (num, formSingle, formPlural) =>
  num > 1 ? `${num} ${formPlural}` : `${num} ${formSingle}`

const pluralWithoutNumber = (num, formSingle, formPlural) =>
  num > 1 ? formPlural : formSingle

const getUniqueUsersFromEvents = (
  events: NotificationEvent[],
): NotificationObject[] => {
  return lodashUniqBy(
    events.map(event => event.user),
    'id',
  )
}

const filterRecentEvents = (item, events) => {
  return events.filter(
    event =>
      !item.marked_as_read ||
      new Date(event.timestamp) >= new Date(item.marked_as_read),
  )
}

const getUniqueTracksFromEvents = (
  events: NotificationEvent[],
): NotificationObject[] => {
  return lodashUniqBy(
    events.map(event => event.track),
    'id',
  )
}

const isUsersEqual = (users1, users2) => {
  return (
    lodashIntersection(
      users1.map(user => user.id),
      users2.map(user => user.id),
    ).length === users1.length
  )
}

const isLoggedUser = (user: NotificationObject): boolean => {
  return Boolean(user.id)
}

const hasAnonymousUser = (users: NotificationObject[]): boolean => {
  // anonymous user has id: null
  return users.some(user => !isLoggedUser(user))
}

const hasLoggedUser = (users: NotificationObject[]): boolean => {
  return users.some(user => isLoggedUser(user))
}

function injectFauxPlaylistAccess(
  item: TAggregatedNotificationItem,
): TAggregatedNotificationItem {
  let editingItem = item

  for (let event2 of [].concat(
    item.playlist_downloads,
    item.track_downloads,
    item.track_streams,
  )) {
    let ok = editingItem.playlist_accesses.some(
      // works even for null
      x => x.user.id === event2.user.id,
    )

    if (!ok) {
      // playlist_access is missing, but it certainly happened for that user
      // generating faux playlist_access to fix notification summary
      let event1 = { ...event2, track: undefined, object_id: item.playlist_id }
      // inject
      console.log('injecting', event1)
      editingItem = {
        ...editingItem,
        playlist_accesses: editingItem.playlist_accesses.concat(event1),
      }
    }
  }

  return editingItem
}

export default class NotificationItem extends Component<Props, State> {
  dismissTimer: ReturnType<typeof setTimeout> | null = null
  state: State = {
    isDismissing: false,
  }

  componentWillUnmount() {
    clearTimeout(this.dismissTimer)
  }

  _shouldOpenUrl(): boolean {
    const { item } = this.props
    const { type } = item

    switch (type) {
      case 'aggregated':
      case 'aggregated_stats_playlist_accesses':
      case 'aggregated_stats_playlist_downloads':
      case 'aggregated_stats_track_downloads':
      case 'aggregated_stats_track_streams':
        return false

      case 'access_share':
        return true

      case 'client_library_access_granted':
        return true

      case 'receive_share':
        if (item.redirect_to_public_page) {
          // open public share page in a new tab
          return true
        }

        return false

      case 'group_receive_share':
      case 'send_upload':
      case 'group_receive_upload':
      case 'receive_upload':
      case 'bulk_upload_complete':
      case 'add_to_disco_complete':
      case 'client_library_access_requested':
      case 'watermark_leak_trace_update':
      case 'welcome_to_disco':
      case 'licensing_term_notifications':
      case 'channel_share_invite_accepted':
      case 'receive_brief_submission':
        return false

      default:
        captureError(new Error('_shouldOpenUrl: unknown type ' + type), {
          moduleName: 'NotificationItem',
        })
        return false
    }
  }

  _handleTooltipUsersClick(evt: React.SyntheticEvent<HTMLElement>) {
    evt.preventDefault()
    this.props.onExpandNotification(this.props.item)
  }

  _handleOpenLibraryClients(evt: React.SyntheticEvent<HTMLElement>) {
    evt.preventDefault()
    this.props.onOpenLibraryClients(this.props.item)
  }

  _handleClick: (evt: React.MouseEvent<HTMLAnchorElement>) => void = (
    evt: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (this._shouldOpenUrl()) return
    evt.preventDefault()
    const { item } = this.props

    switch (item.type) {
      case 'receive_share':
      case 'receive_upload':
      case 'add_to_disco_complete':
        this._handleOpenPlaylist(item, evt)

        return

      case 'aggregated':
      case 'aggregated_stats_playlist_accesses':
      case 'aggregated_stats_playlist_downloads':
      case 'aggregated_stats_track_downloads':
      case 'aggregated_stats_track_streams':
        this._handleTooltipUsersClick(evt)

        return

      case 'client_library_access_requested':
        this._handleOpenLibraryClients(evt)

        return

      case 'licensing_term_notifications':
        this._handleOpenTrack(item, evt)

        return

      default:
        break
    }

    this.props.onOpenNotification(item)
  }
  _handleDismiss: (event: React.SyntheticEvent<EventTarget>) => void = (
    event: React.SyntheticEvent<HTMLElement>,
  ) => {
    const { item, onDismiss } = this.props
    event.preventDefault()
    event.stopPropagation()
    this.setState({
      isDismissing: true,
    })
    this.dismissTimer = setTimeout(() => {
      onDismiss(item)
    }, 300)
  }
  _handleOpenTrack: (
    item: TNotificationItem,
    evt: React.SyntheticEvent<EventTarget>,
  ) => void = (
    item: TNotificationItem,
    evt: React.SyntheticEvent<HTMLElement>,
  ) => {
    if (this._shouldOpenUrl()) return
    evt.stopPropagation()
    evt.preventDefault()
    this.props.onOpenTrackRoute(item)
  }

  _handleOpenPlaylist(
    item: TNotificationItem,
    evt: React.SyntheticEvent<HTMLElement>,
  ) {
    if (this._shouldOpenUrl()) return
    evt.stopPropagation()
    evt.preventDefault()
    this.props.onOpenPlaylist(item)
  }

  _handleOpenBriefSubmission(
    item: BriefSubmissionNotification,
    evt: React.SyntheticEvent<HTMLElement>,
  ) {
    if (this._shouldOpenUrl()) return
    evt.stopPropagation()
    evt.preventDefault()
    this.props.onOpenBriefSubmission(item)
  }

  _handleAcceptChannelInvite(
    item: TNotificationItem,
    evt: React.SyntheticEvent<HTMLElement>,
  ) {
    evt.preventDefault()

    this._handleDismiss(evt)

    this.props.onAcceptChannelInvite(item)
  }

  render(): React.ReactElement<'div'> {
    const { item, timelineItem, expanded, hasDismissButton } = this.props
    const { isDismissing } = this.state
    const name =
      item.sender_name ||
      ('accessor_name' in item ? item.accessor_name : undefined)

    const dismissingClass = isDismissing ? 'NotificationItem_IsDismissing' : ''
    const hasTimelineClass = !!timelineItem
      ? 'NotificationItem_HasTimeline'
      : ''
    const timeItem = <div>{this._renderTime(item.timestamp)}</div>

    const button = this._renderButton(item)

    return (
      <div className={`NotificationItem ${dismissingClass}`}>
        <div className={`NotificationItem_Content ${dismissingClass}`}>
          <a
            href={this._shouldOpenUrl() ? item.url || '#' : '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={
              this._shouldOpenUrl()
                ? () => this.props.onClickLink(item)
                : this._handleClick
            }
          >
            {timelineItem}
          </a>
          <div className="NotificationItem_TextContainer">
            <div className="NotificationItem_Text">
              {this._renderText(item)}
            </div>
            {!hasDismissButton || isDismissing ? null : (
              <div
                className="NotificationItem_Action"
                onClick={this._handleDismiss}
              >
                <Icon type="close" width="24" height="24" />
              </div>
            )}
          </div>
          <div className="NotificationItem_Bottom">
            {button && <div>{button}</div>}
            <div className="NotificationItem_Time">
              <HelpTooltip base={timeItem}>
                {this._renderFulltime(item.timestamp)}
              </HelpTooltip>
            </div>
          </div>
        </div>
      </div>
    )
  }

  _truncate(text: string = '', maxLength: number): string {
    const separator = '...'
    if (text.length < maxLength) return text
    if (separator.length > maxLength) return text.slice(text.length - maxLength)
    const pad = -0.5 * (maxLength - text.length - separator.length)
    const center = text.length / 2
    return (
      text.slice(0, center - pad) +
      separator +
      text.slice(text.length - center + pad)
    )
  }

  _renderObjectsWithName(
    users: NotificationObject[],
    num: string | number,
    pluralText: string,
  ): string {
    if (users.length === 0) {
      return ''
    } else if (users.length === 1) {
      return users[0].name
    } else if (users.length === 2) {
      return `${users[0].name} and ${users[1].name}`
    } else if (users.length === 3) {
      return `${users[0].name}, ${users[1].name} and ${users[2].name}`
    } else {
      return `${num} ${pluralText}`
    }
  }

  _renderUsers(
    users: NotificationObject[],
    options: {
      startcase?: boolean
    } = {},
  ): React.ReactNode | React.ReactElement<'span'> {
    const { startcase } = options
    const loggedUsers = users.filter(user => user.id)
    const loggedWithAnon = hasAnonymousUser(users)
      ? loggedUsers.concat({
          name:
            startcase && users.length === 1 ? 'Other people' : 'other people',
        })
      : loggedUsers
    const noTooltip = loggedWithAnon.length <= 3

    if (noTooltip) {
      return (
        <span>
          {this._renderObjectsWithName(
            loggedWithAnon,
            loggedWithAnon.length,
            'people',
          )}
        </span>
      )
    } else {
      const approxNum = hasAnonymousUser(users)
        ? loggedUsers.length + '+'
        : loggedUsers.length
      return (
        <HelpTooltip
          base={
            <span
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleTooltipUsersClick(evt)
              }
            >
              {this._renderObjectsWithName(loggedWithAnon, approxNum, 'people')}
            </span>
          }
        >
          {loggedUsers.map((item, index) => (
            <div key={index}>{item.name}</div>
          ))}
        </HelpTooltip>
      )
    }
  }

  _renderTracks(
    tracks: NotificationObject[],
  ): React.ReactNode | React.ReactElement<'span'> {
    const num = tracks.length

    if (num <= 2) {
      return (
        <span className="NotificationItem_Object">
          {this._renderObjectsWithName(tracks, num, 'tracks')}
        </span>
      )
    } else {
      return (
        <HelpTooltip
          base={
            <span
              className="NotificationItem_Object"
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleTooltipUsersClick(evt)
              }
            >
              {this._renderObjectsWithName(tracks, num, 'tracks')}
            </span>
          }
        >
          {tracks.map((item, index) => (
            <div key={index}>{item.name}</div>
          ))}
        </HelpTooltip>
      )
    }
  }

  _renderAction(
    item: TAggregatedNotificationItem,
    totalUsers: NotificationObject[],
  ): React.ReactElement<'span'> {
    const {
      playlist_name,
      playlist_accesses,
      playlist_downloads,
      track_downloads,
      track_streams,
    } = item
    let accessedUsersData = getUniqueUsersFromEvents(
      filterRecentEvents(item, playlist_accesses),
    )
    const downloadedUsersData = getUniqueUsersFromEvents(
      filterRecentEvents(item, playlist_downloads),
    )

    if (accessedUsersData.length === 0) {
      // fallback
      // needed for a rare case, where playlist was downloaded *without* being accessed
      // e.g. playlist was accessed, notification was marked as read, playlist was downloaded
      accessedUsersData = totalUsers
    }

    const accessedUsersEl = this._renderUsers(accessedUsersData, {
      startcase: true,
    })

    const downloadedUsersEl = this._renderUsers(downloadedUsersData)

    const playlist = (
      <span
        className="NotificationItem_Object"
        onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
          this._handleOpenPlaylist(item, evt)
        }
      >
        {playlist_name}
      </span>
    )
    const trackDownloadsData = getUniqueTracksFromEvents(
      filterRecentEvents(item, track_downloads),
    )
    const trackStreamsData = getUniqueTracksFromEvents(
      filterRecentEvents(item, track_streams),
    )
    const trackDownloadsNum = trackDownloadsData.length
    const trackStreamsNum = trackStreamsData.length

    if (hasLoggedUser(accessedUsersData)) {
      if (downloadedUsersData.length > 0) {
        if (isUsersEqual(accessedUsersData, downloadedUsersData)) {
          return (
            <span>
              {accessedUsersEl} accessed and downloaded your playlist {playlist}
              .
            </span>
          )
        } else {
          return (
            <span>
              {accessedUsersEl} accessed and {downloadedUsersEl} downloaded your
              playlist {playlist}.
            </span>
          )
        }
      } else if (trackDownloadsNum > 0 && trackStreamsNum > 0) {
        // show them in different sentence
        return (
          <span>
            {accessedUsersEl} accessed your playlist {playlist}. There have been{' '}
            {plural(trackDownloadsNum, 'track download', 'track downloads')} and{' '}
            {plural(trackStreamsNum, 'stream', 'streams')}.
          </span>
        )
      } else if (trackDownloadsNum > 0 && trackStreamsNum === 0) {
        // join them into first sentence
        return (
          <span>
            {accessedUsersEl} accessed your playlist {playlist} and downloaded{' '}
            {plural(trackDownloadsNum, 'track', 'tracks')}.
          </span>
        )
      } else if (trackDownloadsNum === 0 && trackStreamsNum > 0) {
        // join them into first sentence
        return (
          <span>
            {accessedUsersEl} accessed your playlist {playlist} and streamed{' '}
            {plural(trackStreamsNum, 'track', 'tracks')}.
          </span>
        )
      } else {
        return (
          <span>
            {accessedUsersEl} accessed your playlist {playlist}.
          </span>
        )
      }
    } else {
      if (downloadedUsersData.length > 0) {
        // show only downloads
        return (
          <span>Your playlist {playlist} was accessed and downloaded.</span>
        )
      } else if (trackDownloadsNum > 0 && trackStreamsNum > 0) {
        // show them in different sentence
        return (
          <span>
            Your playlist {playlist} was accessed. There have been{' '}
            {plural(trackDownloadsNum, 'track download', 'track downloads')} and{' '}
            {plural(trackStreamsNum, 'stream', 'streams')}.
          </span>
        )
      } else if (trackDownloadsNum > 0 && trackStreamsNum === 0) {
        // join them into first sentence
        return (
          <span>
            Your playlist {playlist} was accessed and{' '}
            {plural(trackDownloadsNum, 'track was', 'tracks were')} downloaded.
          </span>
        )
      } else if (trackDownloadsNum === 0 && trackStreamsNum > 0) {
        // join them into first sentence
        return (
          <span>
            Your playlist {playlist} was accessed and{' '}
            {plural(trackStreamsNum, 'track was', 'tracks were')} streamed.
          </span>
        )
      } else {
        return <span>Your playlist {playlist} was accessed.</span>
      }
    }
  }

  _renderMore(
    item: TAggregatedNotificationItem,
  ): null | React.ReactElement<'span'> {
    const {
      recent_track_downloads_num: track_downloads,
      recent_track_streams_num: track_streams,
    } = item

    if (track_downloads > 0 || track_streams > 0) {
      return (
        <span>
          {' '}
          Click{' '}
          <span className="NotificationItem_Object" onClick={this._handleClick}>
            here
          </span>{' '}
          for full stats.
        </span>
      )
    } else {
      return null
    }
  }

  _renderText(
    item: TNotificationItem,
  ): null | React.ReactElement<'div'> | React.ReactElement<'span'> {
    switch (item.type) {
      case 'aggregated': {
        const {
          playlist_accesses,
          playlist_downloads,
          track_downloads,
          track_streams,
        } = item
        const totalUsers = getUniqueUsersFromEvents(
          filterRecentEvents(
            item,
            playlist_accesses
              .concat(playlist_downloads)
              .concat(track_downloads)
              .concat(track_streams),
          ),
        )
        const patchedItem = injectFauxPlaylistAccess(item)
        return (
          <div>
            {this._renderAction(patchedItem, totalUsers)}
            {this._renderMore(patchedItem)}
          </div>
        )
      }

      case 'aggregated_stats_playlist_accesses': {
        const {
          playlist_accesses, // not used, but could be
          // total_playlist_accesses_num
        } = item
        const users = getUniqueUsersFromEvents(playlist_accesses)

        if (!hasLoggedUser(users)) {
          return <div>Your playlist was accessed by an anonymous user</div>
        } else {
          return (
            <div>
              {this._renderUsers(users, {
                startcase: true,
              })}{' '}
              accessed your playlist
            </div>
          )
        }
      }

      case 'aggregated_stats_playlist_downloads': {
        const { playlist_downloads } = item
        const users = getUniqueUsersFromEvents(playlist_downloads)

        if (!hasLoggedUser(users)) {
          return <div>Your playlist was downloaded by an anonymous user</div>
        } else {
          return (
            <div>
              {this._renderUsers(users, {
                startcase: true,
              })}{' '}
              downloaded your playlist
            </div>
          )
        }
      }

      case 'aggregated_stats_track_downloads': {
        const { track_downloads } = item
        const users = getUniqueUsersFromEvents(track_downloads)
        const tracks = getUniqueTracksFromEvents(track_downloads)
        const nodeTracks = (
          <span className="NotificationItem_Object">
            {this._renderTracks(tracks)}
          </span>
        )

        if (!hasLoggedUser(users)) {
          return (
            <div>
              {nodeTracks} {pluralWithoutNumber(tracks.length, 'was', 'were')}{' '}
              downloaded by an anonymous user
            </div>
          )
        } else {
          return (
            <div>
              {this._renderUsers(users, {
                startcase: true,
              })}{' '}
              downloaded {nodeTracks}
            </div>
          )
        }
      }

      case 'aggregated_stats_track_streams': {
        const { track_streams } = item
        const users = getUniqueUsersFromEvents(track_streams)
        const tracks = getUniqueTracksFromEvents(track_streams)
        const nodeTracks = (
          <span className="NotificationItem_Object">
            {this._renderTracks(tracks)}
          </span>
        )

        if (hasAnonymousUser(users)) {
          return (
            <div>
              {nodeTracks} {pluralWithoutNumber(tracks.length, 'was', 'were')}{' '}
              streamed by an anonymous user
            </div>
          )
        } else {
          return (
            <div>
              {this._renderUsers(users, {
                startcase: true,
              })}{' '}
              streamed {nodeTracks}
            </div>
          )
        }
      }

      // TODO: DEPRECATED ('access_share' is not used)
      case 'access_share': {
        const { accessor_name, content_label } = item
        return (
          <span>
            <span>{this._truncate(accessor_name, MAX_NAME_LENGTH)}</span> has
            accessed your playlist{' '}
            <span className="NotificationItem_Object">{content_label}</span>
          </span>
        )
      }

      case 'group_receive_share':
      case 'receive_share': {
        const { sender_name, content_label } = item
        return (
          <span>
            You’ve received new files from{' '}
            <span>{this._truncate(sender_name, MAX_NAME_LENGTH)}: </span>
            {this._shouldOpenUrl() ? (
              <a href={item.url} target="_blank" rel="noreferrer">
                {content_label}
              </a>
            ) : (
              <span
                className="NotificationItem_Object"
                onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                  this._handleOpenPlaylist(item, evt)
                }
              >
                {content_label}
              </span>
            )}
          </span>
        )
      }

      case 'receive_brief_submission': {
        const { sender_name, content_label } = item
        return (
          <span>
            You’ve received new brief submissions from{' '}
            <span>{this._truncate(sender_name, MAX_NAME_LENGTH)}: </span>
            <span
              className="NotificationItem_Object"
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleOpenBriefSubmission(item, evt)
              }
            >
              {content_label}
            </span>
          </span>
        )
      }

      case 'send_upload':
      case 'group_receive_upload':
      case 'receive_upload':
        const { sender_name, playlist } = item
        return (
          <span>
            You’ve received new files from{' '}
            <span>{this._truncate(sender_name, MAX_NAME_LENGTH)}: </span>
            <span
              className="NotificationItem_Object"
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleOpenPlaylist(item, evt)
              }
            >
              {playlist.name}
            </span>
          </span>
        )

      case 'add_to_disco_complete': {
        const { content_label } = item
        return (
          <span>
            We’ve added{' '}
            <span
              className="NotificationItem_Object"
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleOpenPlaylist(item, evt)
              }
            >
              {content_label}
            </span>{' '}
            to your DISCO
          </span>
        )
      }

      case 'bulk_upload_complete': {
        const { content_label } = item
        return (
          <span>
            Bulk upload for{' '}
            <span className="NotificationItem_Object">{content_label}</span> is
            complete
          </span>
        )
      }

      case 'client_library_access_requested': {
        const username = item.sender_name || item.sender_email
        return (
          <span>
            <span>{username}</span> has requested access to your Catalog{' '}
            <span className="NotificationItem_Object">
              {item.client_library_name}
            </span>
            . Click{' '}
            <Link
              className="NotificationItem_Object"
              to={`/catalogs/${item.client_library_id}/clients`}
            >
              here
            </Link>{' '}
            to action this request.
          </span>
        )
      }

      case 'client_library_access_granted': {
        const name = item.client_library_name
        const url = item.client_library_url
        const business = item.business.name
        return (
          <span>
            You have been granted access to{' '}
            <a
              className="NotificationItem_Object"
              target="_blank"
              rel="noopener noreferrer"
              href={url}
            >
              {name}
            </a>{' '}
            -
            <a
              className="NotificationItem_Object"
              target="_blank"
              rel="noopener noreferrer"
              href={url}
            >
              Browse their Catalog now
            </a>
            .
          </span>
        )
      }

      case 'watermark_leak_trace_update':
        return (
          <span className="NotificationItem_Object">
            Your watermarking leak trace is complete.
            <br /> View the results{' '}
            <Link
              className="NotificationItem_Link"
              to="/watermarking/leak-tracing"
            >
              here.
            </Link>
          </span>
        )

      case 'licensing_term_notifications':
        return (
          <div>
            License term for{' '}
            <span
              className="NotificationItem_Object"
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleOpenTrack(item, evt)
              }
            >
              {item.track.name}
            </span>{' '}
            expires on{' '}
            <span
              className="NotificationItem_Object"
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleOpenTrack(item, evt)
              }
            >
              {dayjs(item.expiration_date).format('YYYY-MM-DD')}{' '}
            </span>{' '}
          </div>
        )

      case 'welcome_to_disco':
        return (
          <div>
            Welcome to DISCO! This is where your notifications will appear.{' '}
            <span
              className="NotificationItem_Object"
              onClick={(evt: React.SyntheticEvent<HTMLElement>) =>
                this._handleOpenPlaylist(item, evt)
              }
            >
              You have a message from us in your inbox
            </span>
            .
          </div>
        )

      case 'channel_share_invite': {
        const { inviting_business_name, channel_name } = item
        return (
          <span>
            {inviting_business_name} has invited you to their shared channel ’
            <strong>{channel_name}</strong>’.
          </span>
        )
      }

      case 'channel_share_invite_accepted': {
        const { invited_business_name, channel_name } = item
        return (
          <span>
            {invited_business_name} has accepted the invite to your ’
            <strong>{channel_name}</strong>’ channel.
          </span>
        )
      }

      default:
        captureError(
          new Error('no text for ' + (item as { type: string }).type),
          {
            moduleName: 'NotificationItem',
          },
        )
        return null
    }
  }

  _renderButton(item: TNotificationItem): null | React.ReactNode {
    switch (item.type) {
      case 'channel_share_invite': {
        return (
          <Button
            className="NotificationItem_Button"
            caption="Add to my DISCO"
            appearance="inline-link"
            onClick={evt => this._handleAcceptChannelInvite(item, evt)}
          />
        )
      }

      default:
        return null
    }
  }

  _renderFulltime(timestamp: string): string {
    return dayjs(timestamp).format('h:mm A DD/MM/YY')
  }

  _renderTime(timestamp: string): string {
    const { timelineItem, expanded } = this.props

    if (timelineItem) {
      return dayjs(timestamp).format('h:mm A')
    } else {
      if (expanded) {
        return this._renderFulltime(timestamp)
      } else {
        return getRelativeTime(timestamp)
      }
    }
  }
}
